import { memo } from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, DollarSign, Eye, Lock } from "lucide-react";
import CryptoCard from "@/components/game/cards/CryptoCard";
import EffectCard from "@/components/game/cards/EffectCard";

/**
 * Bottom "player area" footer: portfolio (left), stablecoin pile (middle),
 * and hand (right). The ONLY interactive bits are:
 *   - click a portfolio card during the decision phase to select for Sell
 *   - click a hand card to inspect → confirm play
 *   - click a hand card during the discard phase to discard
 *
 * `memo`-wrapped (KNOWN_ISSUES §1.36) — re-renders this dozens-of-cards
 * footer on every state update were a meaningful share of the wasted
 * work flagged by the audit.
 */
function PlayerArea({
  currentPlayer,
  phase,
  selectedSellCard,
  setSelectedSellCard,
  onInspectCard,
  onDiscardCard,
}) {
  const { t } = useTranslation();
  const holdableInHand = currentPlayer.hand.filter((c) => !c.persistent_symbol).length;
  const persistentInHand = currentPlayer.hand.filter((c) => c.persistent_symbol).length;

  return (
    // sticky bottom-0 keeps the portfolio + hand row pinned to the
    // viewport floor so the player's cards never scroll off the
    // bottom of the screen mid-game. The rest of the board (event
    // log, decks, scoreboard) scrolls above. z-30 sits below modals
    // (z-40+) and the reconnecting overlay (z-60) but above the
    // standard board content.
    <footer
      className="sticky bottom-0 z-30 border-t border-[#1A237E]/10 dark:border-[#E3F2FD]/10 bg-white/85 dark:bg-slate-900/85 backdrop-blur"
      data-testid="player-area"
    >
      {/* Mobile-only compact stablecoins pill. The desktop Stablecoins
          block (right column) takes ~110px of vertical space — too
          expensive on a 700-ish px tall phone where the user also
          needs to see Portfolio + Hand. Hidden ≥sm; the full block
          lives in the grid below for desktop. */}
      <div
        className="sm:hidden flex items-center justify-between gap-2 px-3 py-1.5 border-b border-[#1A237E]/10 dark:border-[#E3F2FD]/10"
        data-testid="player-area-stablecoins-mobile"
      >
        <span className="flex items-center gap-1.5 text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD]">
          <DollarSign className="w-3.5 h-3.5 text-green-500" />
          {t("playerArea.labels.stablecoins")}
        </span>
        <span className="flex items-center gap-2">
          <span
            className="text-lg font-bold text-green-600 tabular-nums"
            style={{ fontFamily: "Fredoka" }}
          >
            ${currentPlayer.stablecoin_available}
          </span>
          {currentPlayer.stablecoin_locked > 0 && (
            <span className="text-[10px] font-bold text-[#FF5A5F] flex items-center gap-0.5">
              <Lock className="w-3 h-3" /> ${currentPlayer.stablecoin_locked}
            </span>
          )}
        </span>
      </div>

      <div className="flex flex-col sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-start gap-2 sm:gap-4 p-2 sm:p-3 min-w-0">
        {/* Portfolio — left column. min-w-0 lets the inner flex-row
            with overflow-x-auto actually clip when crypto cards exceed
            the column width. */}
        <div className="min-w-0 w-full sm:w-auto sm:justify-self-start sm:max-w-full">
          <div className="flex items-center gap-2 mb-1 sm:mb-2">
            <TrendingUp className="w-4 h-4 text-[#38B6FF]" />
            <span className="text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD]">
              {t("playerArea.portfolio", { count: currentPlayer.portfolio.length })}
            </span>
            <span className="text-xs text-[#38B6FF] font-bold">
              ${currentPlayer.crypto_value}
            </span>
          </div>
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1">
            {currentPlayer.portfolio.length === 0 ? (
              <div className="border-2 border-dashed border-[#1A237E]/15 dark:border-[#E3F2FD]/15 rounded-xl px-6 py-4 text-xs text-[#1A237E]/40 dark:text-[#E3F2FD]/40">
                {t("playerArea.labels.noCrypto")}
              </div>
            ) : (
              currentPlayer.portfolio.map((card) => (
                <CryptoCard
                  key={card.id}
                  card={card}
                  selected={selectedSellCard === card.id}
                  assetBonus={currentPlayer.asset_bonuses?.[card.symbol] || 0}
                  onClick={() => {
                    if (phase === "decision")
                      setSelectedSellCard(
                        selectedSellCard === card.id ? null : card.id,
                      );
                  }}
                />
              ))
            )}
          </div>
        </div>

        {/* Hand — visually centered between the side columns via the
            outer `grid-cols-[1fr_auto_1fr]`. min-w-0 keeps the inner
            card row's overflow-x-auto able to clip cleanly. */}
        <div className="min-w-0 w-full sm:w-auto sm:max-w-md sm:justify-self-center">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-[#8247E5]" />
            <span className="text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD]">
              {t("playerArea.hand", { count: holdableInHand, persistent: persistentInHand > 0 ? t("playerArea.handPersistentSuffix", { count: persistentInHand }) : "" })}
            </span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 min-w-0">
            {currentPlayer.hand.length === 0 ? (
              <div className="border-2 border-dashed border-[#1A237E]/15 dark:border-[#E3F2FD]/15 rounded-xl px-6 py-4 text-xs text-[#1A237E]/40 dark:text-[#E3F2FD]/40">
                {t("playerArea.labels.noCards")}
              </div>
            ) : (
              currentPlayer.hand.map((card) => (
                <EffectCard
                  key={card.id}
                  card={card}
                  playable={
                    (phase === "action_event" || phase === "decision") &&
                    currentPlayer.actions_played_this_turn < 1
                  }
                  discardable={phase === "discard"}
                  onInspect={onInspectCard}
                  onPlay={() => onInspectCard(card)}
                  onDiscard={() => onDiscardCard(card.id)}
                />
              ))
            )}
          </div>
        </div>

        {/* Stablecoins — right column, anchored to the right edge.
            Hidden on mobile (rendered as a compact pill above). */}
        <div className="hidden sm:block w-full sm:w-40 sm:justify-self-end">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-green-500" />
            <span className="text-xs font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("playerArea.labels.stablecoins")}</span>
          </div>
          <div className="bg-white/70 dark:bg-slate-900/70 rounded-xl p-2 border border-[#1A237E]/10 dark:border-[#E3F2FD]/10">
            <p
              className="text-xl font-bold text-green-600 text-center"
              style={{ fontFamily: "Fredoka" }}
            >
              ${currentPlayer.stablecoin_available}
            </p>
            {currentPlayer.stablecoin_locked > 0 && (
              <p className="text-xs text-center text-[#FF5A5F] flex items-center justify-center gap-1 mt-1">
                <Lock className="w-3 h-3" /> {t("playerArea.labels.locked", { amount: currentPlayer.stablecoin_locked })}
              </p>
            )}
            <div className="flex flex-wrap gap-1 mt-2 justify-center">
              {currentPlayer.stablecoins.map((sc) => (
                <span
                  key={sc.id}
                  className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                    sc.locked
                      ? "bg-red-100 text-red-600 line-through"
                      : sc.denomination === 200
                      ? "bg-green-100 text-green-700"
                      : sc.denomination === 100
                      ? "bg-blue-100 text-blue-700"
                      : "bg-indigo-100 text-indigo-700"
                  }`}
                >
                  ${sc.denomination}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default memo(PlayerArea);
